import { useQuery } from '@tanstack/react-query';
import { userManagementApi } from '../api/userManagementApi';
import { userManagementQueryKeys } from './queryKeys';
import { UseUserWithRoleOptions, UserWithRoleResult, DEFAULT_STALE_TIME } from '../types';

/**
 * Hook for fetching a single user with role information by user ID
 * @param userId The ID of the user to fetch
 * @param tenantId Optional tenant ID for scoped queries
 * @param options React Query options
 * @returns UserWithRoleResult with data, loading state, and controls
 */
export const useUserWithRole = (
  userId: string,
  tenantId?: string,
  options: UseUserWithRoleOptions = {}
): UserWithRoleResult => {
  const query = useQuery({
    queryKey: userManagementQueryKeys.userWithRole(userId, tenantId),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const response = await userManagementApi.getUserWithRoleById(userId, tenantId);
      return response.data;
    },
    enabled: options.enabled !== false && !!userId,
    staleTime: options.staleTime || DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return typeof options.retry === 'number'
        ? failureCount < options.retry
        : typeof options.retry === 'boolean'
          ? options.retry
          : failureCount < 3;
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};

/**
 * Hook for fetching a user without role information (lighter query)
 * @param userId The ID of the user to fetch
 * @param options React Query options
 * @returns Query result with basic user data
 */
export const useUser = (userId: string, options: UseUserWithRoleOptions = {}) => {
  return useQuery({
    queryKey: userManagementQueryKeys.user(userId),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const response = await userManagementApi.getUserById(userId);
      return response.data;
    },
    enabled: options.enabled !== false && !!userId,
    staleTime: options.staleTime || DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return typeof options.retry === 'number'
        ? failureCount < options.retry
        : typeof options.retry === 'boolean'
          ? options.retry
          : failureCount < 3;
    },
  });
};

/**
 * Hook for fetching multiple users without role information
 * @param tenantId Optional tenant ID for scoped queries
 * @param options React Query options
 * @returns Query result with basic users data
 */
export const useUsers = (tenantId?: string, options: UseUserWithRoleOptions = {}) => {
  return useQuery({
    queryKey: userManagementQueryKeys.users(tenantId),
    queryFn: async () => {
      const response = await userManagementApi.getUsers(tenantId);
      return response.data || [];
    },
    enabled: options.enabled !== false,
    staleTime: options.staleTime || DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return typeof options.retry === 'number'
        ? failureCount < options.retry
        : typeof options.retry === 'boolean'
          ? options.retry
          : failureCount < 3;
    },
  });
};
